/** โอนย้ายสต๊อกภายในโรงงาน (D-182) — ย้ายล็อตระหว่างที่เก็บ; ส่ง Idempotency-Key ต่อหนึ่งเนื้อหา (ส่งซ้ำ = คำตอบเดิม ไม่ย้ายซ้ำ) */

import { useRef, useState, type FormEvent } from 'react';

import { RecordPanel } from '../components/RecordPanel';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { postRecord } from '../../lib/backend/records';
import { newIdempotencyKey, stockFailureText } from '../../lib/inventory/stock';

const MODULE = 'INVENTORY';
const EMPTY = { plant_code: '', lot_id: '', from_location_code: '', to_location_code: '', quantity: '', reason: '' };

export interface InternalTransferScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function InternalTransferScreen({ permissions, apiBaseUrl, fetchImpl }: InternalTransferScreenProps) {
  const canCreate = can(permissions, MODULE, 'CREATE');
  const io = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };
  const [form, setForm] = useState(EMPTY);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // คีย์เดียวต่อเนื้อหาเดียว: กดซ้ำหลังเครือข่ายหลุดต้องได้คำตอบเดิม ไม่ใช่การย้ายครั้งที่สอง
  const keyRef = useRef<{ fingerprint: string; key: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    const body = {
      plant_code: form.plant_code.trim().toUpperCase(),
      lot_id: form.lot_id.trim(),
      from_location_code: form.from_location_code.trim().toUpperCase(),
      to_location_code: form.to_location_code.trim().toUpperCase(),
      quantity: form.quantity.trim(),
      ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
    };
    if (!body.plant_code || !body.lot_id || !body.from_location_code || !body.to_location_code || !body.quantity) {
      setError('กรอกโรงงาน ล็อต ที่เก็บต้นทาง/ปลายทาง และจำนวน');
      return;
    }
    const fingerprint = JSON.stringify(body);
    if (keyRef.current === null || keyRef.current.fingerprint !== fingerprint) {
      keyRef.current = { fingerprint, key: newIdempotencyKey() };
    }
    setBusy(true);
    const result = await postRecord('/v1/internal-transfers', body, { 'Idempotency-Key': keyRef.current.key }, io);
    setBusy(false);
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    keyRef.current = null;
    setNotice(`โอนย้ายแล้ว: ${String(result.body.transfer_no ?? '')}`);
    setForm(EMPTY);
    setReload((n) => n + 1);
  };

  const field = (name: keyof typeof EMPTY, label: string) => (
    <label className="cap-field" key={name}>
      <span className="cap-field__label">{label}</span>
      <input
        className="cap-input"
        aria-label={label}
        value={form[name]}
        onChange={(e) => setForm((c) => ({ ...c, [name]: e.target.value }))}
      />
    </label>
  );

  return (
    <div className="cap-page" data-testid="internal-transfer">
      <div className="cap-page__head">
        <h1 className="cap-page__title">โอนย้ายสต๊อกภายในโรงงาน</h1>
      </div>
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="transfer-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="cap-notice__text" role="status" data-testid="transfer-notice">
          {notice}
        </p>
      )}
      <RecordPanel
        path="/v1/internal-transfers"
        columns={[
          { key: 'transfer_no', label: 'เลขที่' },
          { key: 'plant_code', label: 'โรงงาน' },
          { key: 'product_code', label: 'สินค้า' },
          { key: 'from_location_code', label: 'จาก' },
          { key: 'to_location_code', label: 'ไป' },
          { key: 'quantity', label: 'จำนวน' },
          { key: 'created_by_employee_id', label: 'ผู้ทำรายการ' },
          { key: 'created_at', label: 'เมื่อ' },
        ]}
        testId="transfer-table"
        reloadToken={reload}
        {...io}
      />
      {canCreate && (
        <form className="cap-form" data-testid="transfer-form" onSubmit={(e) => void submit(e)}>
          <h2 className="cap-notice__title">โอนย้ายใหม่</h2>
          {field('plant_code', 'รหัสโรงงาน')}
          {field('lot_id', 'รหัสล็อต (lot_id)')}
          {field('from_location_code', 'ที่เก็บต้นทาง')}
          {field('to_location_code', 'ที่เก็บปลายทาง')}
          {field('quantity', 'จำนวน')}
          {field('reason', 'เหตุผล (ไม่บังคับ)')}
          <div className="cap-form__actions">
            <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
              โอนย้าย
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
